/**
 * Online Food Ordering Admin Layout
 * Dashboard layout with sidebar navigation
 */

import {
  Brightness4 as DarkModeIcon,
  Dashboard as DashboardIcon,
  Brightness7 as LightModeIcon,
  Logout as LogoutIcon,
  Menu as MenuIcon,
  Restaurant as MenuItemIcon,
  Receipt as OrdersIcon,
  LocalOffer as PromoIcon,
  Settings as SettingsIcon,
} from '@mui/icons-material';
import {
  AppBar,
  Avatar,
  Box,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Toolbar,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { useState } from 'react';
import { Outlet, Link as RouterLink, useLocation, useNavigate } from 'react-router-dom';

import { useAppStore } from '@/store/useAppStore';
import { useAuthStore } from '@/store/useAuthStore';
import { tn } from '@/utils/i18n';

const DRAWER_WIDTH = 260;

const navItems = [
  {
    label: 'Dashboard',
    path: '/admin/dashboard',
    icon: <DashboardIcon sx={{ fontSize: { xs: '1.25rem', md: '1.5rem' } }} />,
  },
  {
    label: 'Orders',
    path: '/admin/orders',
    icon: <OrdersIcon sx={{ fontSize: { xs: '1.25rem', md: '1.5rem' } }} />,
  },
  {
    label: 'Menu',
    path: '/admin/menu',
    icon: <MenuItemIcon sx={{ fontSize: { xs: '1.25rem', md: '1.5rem' } }} />,
  },
  {
    label: 'Promo Codes',
    path: '/admin/promos',
    icon: <PromoIcon sx={{ fontSize: { xs: '1.25rem', md: '1.5rem' } }} />,
  },
  {
    label: 'Settings',
    path: '/admin/settings',
    icon: <SettingsIcon sx={{ fontSize: { xs: '1.25rem', md: '1.5rem' } }} />,
  },
];

export default function AdminLayout() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const location = useLocation();
  const navigate = useNavigate();

  const [mobileOpen, setMobileOpen] = useState(false);

  const themeMode = useAppStore((state) => state.themeMode);
  const toggleTheme = useAppStore((state) => state.toggleTheme);
  const restaurant = useAppStore((state) => state.restaurant);
  const user = useAuthStore((state) => state.user);
  const logout = useAuthStore((state) => state.logout);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const drawer = (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Header */}
      <Box
        sx={{
          p: { xs: 1.5, md: 2 },
          display: 'flex',
          alignItems: 'center',
          gap: { xs: 1, md: 1.5 },
          borderBottom: 1,
          borderColor: 'divider',
        }}
      >
        <Avatar
          sx={{
            bgcolor: 'primary.main',
            width: { xs: 32, md: 40 },
            height: { xs: 32, md: 40 },
            fontSize: { xs: '1rem', md: '1.25rem' },
          }}
        >
          {tn(restaurant?.name_translations)?.[0] || 'F'}
        </Avatar>
        <Box sx={{ overflow: 'hidden' }}>
          <Typography
            variant="subtitle2"
            fontWeight={600}
            noWrap
            sx={{ fontSize: { xs: '0.875rem', md: '1rem' } }}
          >
            {tn(restaurant?.name_translations) || 'Sulthan Gulf Kitchen'}
          </Typography>
          <Typography
            variant="caption"
            color="text.secondary"
            noWrap
            sx={{ fontSize: { xs: '0.7rem', md: '0.75rem' } }}
          >
            Admin Panel
          </Typography>
        </Box>
      </Box>

      {/* Navigation */}
      <List sx={{ flex: 1, py: { xs: 1.5, md: 2 } }}>
        {navItems.map((item) => (
          <ListItem key={item.path} disablePadding sx={{ px: { xs: 1, md: 1.5 }, mb: 0.5 }}>
            <ListItemButton
              component={RouterLink}
              to={item.path}
              selected={location.pathname === item.path}
              onClick={() => isMobile && setMobileOpen(false)}
              sx={{
                borderRadius: 2,
                py: { xs: 1, md: 1.5 },
                '&.Mui-selected': {
                  backgroundColor: 'primary.main',
                  color: 'white',
                  '&:hover': {
                    backgroundColor: 'primary.dark',
                  },
                  '& .MuiListItemIcon-root': {
                    color: 'white',
                  },
                },
              }}
            >
              <ListItemIcon sx={{ minWidth: { xs: 36, md: 40 } }}>{item.icon}</ListItemIcon>
              <ListItemText
                primary={item.label}
                primaryTypographyProps={{
                  fontSize: { xs: '0.875rem', md: '1rem' },
                  fontWeight: 500,
                }}
              />
            </ListItemButton>
          </ListItem>
        ))}
      </List>

      <Divider />

      {/* Footer */}
      <Box sx={{ p: { xs: 1.5, md: 2 } }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: { xs: 0.75, md: 1 },
            p: { xs: 1, md: 1.5 },
            backgroundColor: 'action.hover',
            borderRadius: 2,
            mb: 1,
          }}
        >
          <Avatar
            sx={{
              width: { xs: 32, md: 36 },
              height: { xs: 32, md: 36 },
              fontSize: { xs: '0.75rem', md: '0.875rem' },
              bgcolor: 'primary.main',
            }}
          >
            {user?.first_name?.[0] || user?.email?.[0]?.toUpperCase() || 'A'}
          </Avatar>
          <Box sx={{ flex: 1, overflow: 'hidden' }}>
            <Typography
              variant="body2"
              fontWeight={600}
              noWrap
              sx={{ fontSize: { xs: '0.813rem', md: '0.875rem' } }}
            >
              {user?.first_name || 'Admin'}
            </Typography>
            <Typography
              variant="caption"
              color="text.secondary"
              noWrap
              sx={{ fontSize: { xs: '0.7rem', md: '0.75rem' } }}
            >
              {user?.role?.replace('_', ' ').toUpperCase()}
            </Typography>
          </Box>
        </Box>

        <ListItemButton
          onClick={handleLogout}
          sx={{ borderRadius: 2, color: 'error.main', py: { xs: 1, md: 1.5 } }}
        >
          <ListItemIcon sx={{ minWidth: { xs: 36, md: 40 }, color: 'inherit' }}>
            <LogoutIcon sx={{ fontSize: { xs: '1.25rem', md: '1.5rem' } }} />
          </ListItemIcon>
          <ListItemText
            primary="Logout"
            primaryTypographyProps={{
              fontSize: { xs: '0.875rem', md: '1rem' },
            }}
          />
        </ListItemButton>
      </Box>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      {/* App Bar (Mobile) */}
      <AppBar
        position="fixed"
        sx={{
          width: { md: `calc(100% - ${DRAWER_WIDTH}px)` },
          ml: { md: `${DRAWER_WIDTH}px` },
          display: { md: 'none' },
        }}
      >
        <Toolbar>
          <IconButton color="inherit" edge="start" onClick={handleDrawerToggle} sx={{ mr: 2 }}>
            <MenuIcon />
          </IconButton>
          <Typography
            variant="h6"
            noWrap
            component="div"
            sx={{ flexGrow: 1, fontSize: { xs: '1rem', sm: '1.25rem' } }}
          >
            Admin
          </Typography>
          <IconButton color="inherit" onClick={toggleTheme} size="small">
            {themeMode === 'dark' ? (
              <LightModeIcon sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' } }} />
            ) : (
              <DarkModeIcon sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' } }} />
            )}
          </IconButton>
        </Toolbar>
      </AppBar>

      {/* Drawer */}
      <Box component="nav" sx={{ width: { md: DRAWER_WIDTH }, flexShrink: { md: 0 } }}>
        {/* Mobile drawer */}
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{ keepMounted: true }}
          sx={{
            display: { xs: 'block', md: 'none' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: DRAWER_WIDTH,
            },
          }}
        >
          {drawer}
        </Drawer>

        {/* Desktop drawer */}
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', md: 'block' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: DRAWER_WIDTH,
              borderRight: 1,
              borderColor: 'divider',
            },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>

      {/* Main Content */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 3,
          width: { xs: '100%', md: `calc(100% - ${DRAWER_WIDTH}px)` },
          mt: { xs: '56px', md: 0 },
          backgroundColor: 'background.default',
          minHeight: '100vh',
        }}
      >
        <Outlet />
      </Box>
    </Box>
  );
}
